import { useEffect, useState, useMemo } from 'react'
import { useAcademicApi, useLocationApi } from './index'
import { filter, sortBy } from 'lodash'
import { useSelector } from '@ggs/store'

/**
 * @typedef {import("../../definitions").AcademicPeriodRecord} AcademicPeriodRecord
 * @typedef {import("../../definitions").SelectOption} SelectOption
 * @typedef {SelectOption[]} SelectOptions
 */

/**
 * @typedef {Object} CampusSessionDataFetchHook
 * @property {SelectOptions} sessionOptions
 * @property {SelectOptions} campusOptions
 * @property {boolean} loading
 */

/**
 * Hook that fetch UBC campus / session data and compose select options.
 * @return {CampusSessionDataFetchHook}
 */
const useCampusSessionDataFetch = () => {
  const { getAcademicPeriods } = useAcademicApi()
  const { getCampuses } = useLocationApi()
  const campus = useSelector((state) => state.settings.campus)

  /**
   * @type {AcademicPeriodRecord[]}
   */
  const defaultAcademicPeriods = null
  const [academicPeriods, setAcademicPeriods] = useState(defaultAcademicPeriods)

  /**
   * @type {SelectOptions}
   */
  const defaultCampuses = null
  const [campusOptions, setCampusOptions] = useState(defaultCampuses)
  const [loading, setLoading] = useState(true)

  /**
   * Load campus and sessions data through location / academic APIs.
   * @return {Promise<void>}
   */
  const resolveApiData = async () => {
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    const academicPeriodResults = await getAcademicPeriods({
      isStandardPeriod: true,
      academicYear: [currentYear -1, currentYear, currentYear + 1],
      pageSize: 500,
      hasDisplayOnPublicWebsiteTag: true,
    })

    if (academicPeriodResults?.pageItems) {
      setAcademicPeriods(academicPeriodResults.pageItems)
    }

    const campusResults = await getCampuses({
      pageSize: 500,
    })

    const campuses = campusResults?.pageItems
    if (campuses) {
      const composedCampusOptions = campuses.map((campus) => {
        // Ignore records where campus ID don't contain CAM prefix.
        if (!campus.campusCode.includes('UBC')) {
          return
        }

        return {
          label: campus.campusName,
          value: campus.campusCode,
        }
      })

      setCampusOptions(filter(composedCampusOptions))
    }

    setLoading(false)
  }

  /**
   * Compose current selected campus code.
   * @param {string} campusCode
   * @return {string}
   */
  const normalizeCampusCode = (campusCode) => {
    if (!campusCode) {
      return ''
    }

    return 'UBC-' + campusCode.slice(-1)
  }

  /**
   * @type {SelectOptions}
   */
  const sessionOptions = useMemo(() => {
    if (academicPeriods) {
      const campusCode = normalizeCampusCode(campus)
      const composedSessionOptions = academicPeriods.map((academicPeriod) => {
        const academicCalendar = academicPeriod?.academicPeriod?.academicCalendar || ''
        if (academicCalendar.includes(campusCode)) {
          return {
            label: academicPeriod?.academicPeriod?.academicPeriodName,
            value: academicPeriod?.academicPeriod?.academicPeriodId,
          }
        }
      })

      return sortBy(filter(composedSessionOptions), 'label')
    }
  }, [campus, academicPeriods])

  useEffect(() => {
    resolveApiData().then(() => {})
  }, [])

  return {
    sessionOptions,
    campusOptions,
    loading,
  }
}

export default useCampusSessionDataFetch
